import React from 'react';
import { motion } from 'framer-motion';
import { Heart, MapPin, Flower2, UtensilsCrossed, Camera, Music, CalendarCheck, MessageCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
const offerings = [{
  icon: MapPin,
  title: 'Venue Selection',
  description: 'Finding the perfect location that matches your vision and budget.'
}, {
  icon: Flower2,
  title: 'Decor & Florals',
  description: 'Stunning arrangements and decorations to transform your venue.'
}, {
  icon: UtensilsCrossed,
  title: 'Catering Coordination',
  description: 'Delicious menus tailored to your taste and dietary needs.'
}, {
  icon: Camera,
  title: 'Photography Planning',
  description: 'Connecting you with talented photographers to capture every moment.'
}, {
  icon: Music,
  title: 'Entertainment',
  description: 'DJs, live bands, and entertainment that keeps the celebration alive.'
}, {
  icon: CalendarCheck,
  title: 'Day-of Coordination',
  description: 'Seamless execution so you can enjoy every moment stress-free.'
}];
const processSteps = [{
  step: 1,
  title: 'Consultation',
  description: 'We meet to understand your vision, style, and budget.'
}, {
  step: 2,
  title: 'Planning',
  description: 'We create a detailed plan and timeline for your big day.'
}, {
  step: 3,
  title: 'Preparation',
  description: 'We coordinate with vendors and finalize all details.'
}, {
  step: 4,
  title: 'Celebration',
  description: 'We execute flawlessly while you enjoy your wedding.'
}];
export function WeddingsPage() {
  return <div className="min-h-screen" data-id="element-294">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-rose-50 via-white to-rose-100 pt-24" data-id="element-295">
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-296">
          <div className="absolute -top-[20%] -right-[10%] w-[500px] h-[500px] rounded-full bg-rose-200/40 blur-3xl" data-id="element-297" />
          <div className="absolute bottom-[10%] -left-[10%] w-[400px] h-[400px] rounded-full bg-rose-100/50 blur-3xl" data-id="element-298" />
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center py-16" data-id="element-299">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-300">
            <div className="w-20 h-20 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-301">
              <Heart className="w-10 h-10 text-rose-500" data-id="element-302" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-stone-900 mb-6" data-id="element-303">
              Wedding Planning
            </h1>
            <p className="text-xl md:text-2xl text-stone-600 max-w-2xl mx-auto" data-id="element-304">
              Making your dream wedding a reality in Kitwe
            </p>
          </motion.div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 bg-white" data-id="element-305">
        <div className="container mx-auto px-4" data-id="element-306">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-307">
            <h2 className="text-4xl font-bold text-stone-900 mb-4" data-id="element-308">
              What We Offer
            </h2>
            <div className="w-24 h-1 bg-rose-500 mx-auto rounded-full" data-id="element-309" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-310">
            {offerings.map((item, index) => <motion.div key={item.title} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-stone-50 rounded-2xl p-8 border border-stone-100" data-id="element-311">
                <div className="w-14 h-14 bg-rose-100 rounded-xl flex items-center justify-center mb-6" data-id="element-312">
                  <item.icon className="w-7 h-7 text-rose-500" data-id="element-313" />
                </div>
                <h3 className="text-xl font-bold text-stone-900 mb-3" data-id="element-314">
                  {item.title}
                </h3>
                <p className="text-stone-600" data-id="element-315">{item.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 bg-stone-50" data-id="element-316">
        <div className="container mx-auto px-4" data-id="element-317">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-318">
            <h2 className="text-4xl font-bold text-stone-900 mb-4" data-id="element-319">
              Our Process
            </h2>
            <div className="w-24 h-1 bg-rose-500 mx-auto rounded-full" data-id="element-320" />
          </motion.div>

          <div className="relative" data-id="element-321">
            {/* Timeline line - hidden on mobile */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-rose-200 -translate-y-1/2" data-id="element-322" />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-323">
              {processSteps.map((item, index) => <motion.div key={item.step} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.15
            }} className="relative text-center" data-id="element-324">
                  <div className="w-16 h-16 bg-rose-500 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold relative z-10" data-id="element-325">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-stone-900 mb-2" data-id="element-326">
                    {item.title}
                  </h3>
                  <p className="text-stone-600" data-id="element-327">{item.description}</p>
                  {index < processSteps.length - 1 && <ArrowRight className="hidden lg:block absolute top-8 -right-4 w-8 h-8 text-rose-300" data-id="element-328" />}
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-rose-500 text-white" data-id="element-329">
        <div className="container mx-auto px-4 text-center" data-id="element-330">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} data-id="element-331">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-332">
              Ready to plan your wedding?
            </h2>
            <p className="text-xl text-rose-100 mb-10 max-w-2xl mx-auto" data-id="element-333">
              Let's create the wedding of your dreams together. Contact us today
              for a free consultation.
            </p>
            <a href="https://wa.me/260968642042" target="_blank" rel="noopener noreferrer" data-id="element-334">
              <Button size="lg" className="bg-white text-rose-500 hover:bg-rose-50 shadow-lg gap-2" data-id="element-335">
                <MessageCircle className="w-5 h-5" data-id="element-336" />
                Start Planning on WhatsApp
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>;
}